<template>
    <!-- "Use your Show Pass on this show" and its confirmation (16 Oct 2026, mock
         https://claude.ai/artifact/UmTEtS1Yt8S48pgua6LkyZ). Used on the read-only bar
         and in Project Settings. Plain markup on purpose: no new <md-*>, so it adds
         nothing to the vue-material removal. -->
    <span v-if="offered" class="usp" :class="'usp-' + variant">
        <button type="button" class="usp-open" @click="openConfirm">Use your Show Pass on this show</button>
        <div v-if="open" class="usp-scrim" @click.self="close">
            <div class="usp-dlg" role="dialog" aria-modal="true" :aria-label="heading">
                <h3>{{ heading }}</h3>
                <div class="usp-body" v-if="!error">
                    <p>Everyone on this show will be able to edit it, with or without a subscription.</p>
                    <p><strong>A Show Pass stays on the show it's used on.</strong></p>
                    <p>If this project is deleted, the Show Pass associated with it will also be deleted. The Show Pass cannot be reapplied.</p>
                </div>
                <div class="usp-body" v-else>
                    <p class="usp-err" role="alert">{{ error }}</p>
                </div>
                <div class="usp-acts">
                    <template v-if="!error">
                        <button type="button" class="usp-flat" :disabled="busy" @click="close">Cancel</button>
                        <button type="button" class="usp-raised" :disabled="busy" @click="apply">{{ busy ? 'Using…' : 'Use Show Pass' }}</button>
                    </template>
                    <button v-else type="button" class="usp-raised" @click="close">OK</button>
                </div>
            </div>
        </div>
    </span>
</template>

<script>
import { mapGetters } from 'vuex';
import firebase from 'firebase/app';
import 'firebase/functions';
import { canOfferShowPass, applyErrorMessage, appliedNotice } from '@/lib/show-pass-apply';

export default {
    name: 'use-show-pass',
    props: {
        // 'bar' sits on the dark read-only bar; 'settings' in Project Settings.
        variant: { type: String, default: 'settings' }
    },
    data() {
        return { open: false, busy: false, error: '' };
    },
    computed: {
        ...mapGetters(['user', 'project']),
        offered() {
            // Keep the dialog up after success or a refusal even though the offer
            // condition no longer holds (the show is now unlocked).
            return this.open || canOfferShowPass(this.user, this.project);
        },
        heading() {
            return `Use your Show Pass on ${(this.project && this.project.title) || 'this show'}?`;
        }
    },
    methods: {
        openConfirm() {
            this.error = '';
            this.open = true;
        },
        close() {
            if (this.busy) return;
            this.open = false;
            this.error = '';
        },
        async apply() {
            const project = this.project;
            this.busy = true;
            try {
                await firebase.functions().httpsCallable('applyShowPass')({ projectId: project.id });
                this.busy = false;
                this.open = false;
                this.$notify({ group: 'auth', type: 'success', text: appliedNotice(project.title) });
            } catch (e) {
                console.error('[show-pass] apply failed:', e && e.message);
                this.busy = false;
                this.error = applyErrorMessage(e);
            }
        }
    }
};
</script>

<style scoped>
/* Values from the mock: app blue #126c9d, ink #212121 / #616161, red #d32f2f. */
.usp-open {
    font-family: inherit;
    cursor: pointer;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: .02em;
    white-space: nowrap;
}
.usp-bar .usp-open {
    margin-left: 12px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 700;
    color: #37474f;
    background: #fff;
    border: 0;
}
.usp-settings {
    display: block;
}
/* Matches the md-raised md-primary buttons in the same panel (Duplicate Project). */
.usp-settings .usp-open {
    width: calc(100% - 16px);
    min-height: 36px;
    margin: 6px 8px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    white-space: normal;
    letter-spacing: .01em;
    color: #000;
    background: #5ecfdf;
    border: 0;
    border-radius: 2px;
    box-shadow: 0 1px 5px rgba(0, 0, 0, .2), 0 2px 2px rgba(0, 0, 0, .14), 0 3px 1px -2px rgba(0, 0, 0, .12);
}
.usp-settings .usp-open:hover {
    background: #4fc3d4;
}
.usp-scrim {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(0, 0, 0, 0.45);
    text-align: left;
}
.usp-dlg {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 440px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 11px 15px -7px rgba(0, 0, 0, .2), 0 24px 38px 3px rgba(0, 0, 0, .14);
}
.usp-dlg h3 {
    margin: 0;
    padding: 20px 24px 6px;
    font-size: 19px;
    font-weight: 500;
    line-height: 1.3;
    color: #212121;
}
.usp-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 6px 24px 8px;
    font-size: 14px;
    line-height: 1.45;
    color: #616161;
}
.usp-body p {
    margin: 0;
}
.usp-body strong {
    color: #212121;
}
.usp-err {
    font-size: 13.5px;
    color: #d32f2f;
}
.usp-acts {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 12px 12px;
}
.usp-acts button {
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: .02em;
    padding: 10px 14px;
    border-radius: 3px;
    border: 0;
    cursor: pointer;
}
.usp-acts button:disabled {
    opacity: 0.6;
    cursor: default;
}
.usp-flat {
    color: #126c9d;
    background: transparent;
}
.usp-raised {
    color: #fff;
    background: #126c9d;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .2);
}
</style>


// WEBPACK FOOTER //
// use-show-pass.vue?2d073264